import {UncachedEvaluator} from "@cm/graph-v3"
import type {CompactUIDTable} from "@cm/utils"
import {
    NodeEvaluator,
    getEmptyCompileContextData,
    TypeDescriptors,
    NotReady,
    chainDescriptors,
    inputSourceMappingDesc,
    outputAffectedObjectPathPartsDesc,
    mergeComponentOutputs,
} from "@cm/template-nodes"
import type {
    GraphBuilder,
    GraphBuilderScope,
    TemplateContext,
    EvaluatedTemplateInputs,
    EvaluatedTemplateOutputs,
    ObjectPath,
    TemplateNodeContext,
    TemplateNode,
    TemplateGraph,
    NodeClassImpl,
    Inlet,
    Outlet,
    InterfaceDescriptor,
} from "@cm/template-nodes"
import type {MattressComponentGraph} from "#mattress/nodes/component/mattress-component-graph"
import type {
    MattressLocalNodesData,
    MattressOnCompileContext,
    MattressOnFilterActiveContext,
    EvaluatedMattressComponentInputs,
    MattressNodePath,
    EvaluatedMattressComponentOutputs,
    MattressMeshData,
    MattressInfoData,
    MattressComponentGraphData,
    MattressCutData,
} from "#mattress/nodes/component/mattress-component-types"
import {EvaluatedMattressComponentInputsTypeDescriptor, EvaluatedTemplateInputsTypeDescriptor} from "#mattress/nodes/component/mattress-component-types"
import type {MattressCurveData} from "#mattress/nodes/curve/mattress-curve"
import type {MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import {
    type MattressComponentInterfaceDescriptor,
    MattressTemplateInterfaceDescriptorTypeDescriptor,
    MattressComponentInterfaceDescriptorTypeDescriptor,
} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import {evaluateFullMattressMeshData, evaluatePartialMattressMeshData, aliasMattressMeshData} from "#mattress/nodes/mattress-mesh"

const TD = TypeDescriptors

// Root field (template graph) on OnFilterActiveContext / OnCompileContext is not actually used in
// the filter / compile passes of mattress nodes, but it must be present because MattressOn*Context
// extend the base template contexts.
const ROOT_TEMPLATE_GRAPH_STUB = {} as TemplateGraph

const getComponentProperties = (
    idMap: CompactUIDTable<string>,
    graphBuilder: GraphBuilder,
    templateContext: TemplateContext,
    graph: MattressComponentGraph,
) => {
    const activeNodeSet = new Set<TemplateNode>()

    const evaluator = new NodeEvaluator(idMap, graphBuilder.scope(), templateContext, {}, undefined)

    const context: MattressOnFilterActiveContext & TemplateNodeContext = {
        type: "onFilterActive",
        evaluator,
        root: ROOT_TEMPLATE_GRAPH_STUB,
        mattressContext: {
            root: graph,
            isComponentCompilationVisit: true,
        },
        onPostUnskippedNode: (node) => {
            const preSize = activeNodeSet.size
            activeNodeSet.add(node)
            if (activeNodeSet.size === preSize) throw new Error(`Node ${node} is already in activeNodeSet`)
        },
    }

    const resultFilterActive = new UncachedEvaluator(context, true)
    resultFilterActive.getSync(graph)

    return {activeNodeSet}
}

//////////////////////////////////////////////////////////////////////////////////////////////

const CompileRunMattressComponentDescriptor = {
    templateContext: TD.inlet(TD.TemplateContext),

    graphData: TD.inlet(TD.Nullable(TD.Identity<MattressComponentGraphData>("contentHash"))),
    templateInputs: TD.inlet(EvaluatedTemplateInputsTypeDescriptor),
    componentInputs: TD.inlet(EvaluatedMattressComponentInputsTypeDescriptor),
    partialMattressMeshData: TD.inlet(TD.Nullable(TD.Identity<MattressMeshData>())),
    nodePath: TD.inlet(TD.Array(TD.Identity<MattressNodePath[number]>())),
    objectPath: TD.inlet(TD.Array(TD.Identity<TemplateNode>())),
    componentDepth: TD.inlet(TD.Number),
    exposeClaimedSubComponentInputs: TD.inlet(TD.Boolean),
    forwardedLocalInstanceTemplateOutputs: TD.inlet(TD.ShallowJSON<EvaluatedTemplateOutputs>()),
    forwardedLocalInstanceDescriptorList: TD.inlet(TD.Array(MattressTemplateInterfaceDescriptorTypeDescriptor)),

    localNodesDataList: TD.outlet(TD.Nullable(TD.Array(TD.Identity<MattressLocalNodesData>()))),
    curvesDataList: TD.outlet(TD.Nullable(TD.Array(TD.Identity<MattressCurveData>()))),
    referenceFrameDataList: TD.outlet(TD.Nullable(TD.Array(TD.Identity<MattressReferenceFrameData>()))),
    cutsDataList: TD.outlet(TD.Nullable(TD.Array(TD.Identity<MattressCutData>()))),
    componentDescriptorList: TD.outlet(TD.Nullable(TD.Array(MattressComponentInterfaceDescriptorTypeDescriptor))),
    templateDescriptorList: TD.outlet(TD.Nullable(TD.Array(MattressTemplateInterfaceDescriptorTypeDescriptor))),
    componentOutputs: TD.outlet(TD.ShallowJSON<EvaluatedMattressComponentOutputs>()),
    templateOutputs: TD.outlet(TD.ShallowJSON<EvaluatedTemplateOutputs>()),
    fullMattressMeshData: TD.outlet(TD.Nullable(TD.Identity<MattressMeshData>())),
    infoDataList: TD.outlet(TD.Nullable(TD.Array(TD.Identity<MattressInfoData>()))),

    builder: TD.builder(),
}

export class CompileRunMattressComponent implements NodeClassImpl<typeof CompileRunMattressComponentDescriptor, typeof CompileRunMattressComponent> {
    static descriptor = CompileRunMattressComponentDescriptor
    static uniqueName = "CompileRunMattressComponent"

    templateContext!: Inlet<TemplateContext>
    graphData!: Inlet<MattressComponentGraphData | null>
    templateInputs!: Inlet<EvaluatedTemplateInputs>
    componentInputs!: Inlet<EvaluatedMattressComponentInputs>
    partialMattressMeshData!: Inlet<MattressMeshData | null>
    nodePath!: Inlet<MattressNodePath>
    objectPath!: Inlet<ObjectPath>
    componentDepth!: Inlet<number>
    exposeClaimedSubComponentInputs!: Inlet<boolean>
    forwardedLocalInstanceTemplateOutputs!: Inlet<EvaluatedTemplateOutputs>
    forwardedLocalInstanceDescriptorList!: Inlet<InterfaceDescriptor<unknown>[]>

    localNodesDataList!: Outlet<MattressLocalNodesData[] | null>
    curvesDataList!: Outlet<MattressCurveData[] | null>
    referenceFrameDataList!: Outlet<MattressReferenceFrameData[] | null>
    cutsDataList!: Outlet<MattressCutData[] | null>
    componentDescriptorList!: Outlet<MattressComponentInterfaceDescriptor<unknown>[] | null>
    templateDescriptorList!: Outlet<InterfaceDescriptor<unknown>[] | null>
    componentOutputs!: Outlet<EvaluatedMattressComponentOutputs>
    templateOutputs!: Outlet<EvaluatedTemplateOutputs>
    fullMattressMeshData!: Outlet<MattressMeshData | null>
    infoDataList!: Outlet<MattressInfoData[] | null>

    builder!: GraphBuilder

    run() {
        if (this.templateContext === NotReady) return
        if (this.graphData === NotReady) return
        if (this.templateInputs === NotReady) return
        if (this.componentInputs === NotReady) return
        if (this.partialMattressMeshData === NotReady) return
        if (this.nodePath === NotReady) return
        if (this.objectPath === NotReady) return
        if (this.componentDepth === NotReady) return
        if (this.exposeClaimedSubComponentInputs === NotReady) return
        if (this.forwardedLocalInstanceTemplateOutputs === NotReady) return
        if (this.forwardedLocalInstanceDescriptorList === NotReady) return

        if (this.graphData === null) throw new Error("Mattress component graph is null")
        const graph = this.graphData.graph

        const mattressComponentScope = this.builder.scope()
        const {activeNodeSet} = getComponentProperties(this.templateContext.localIds, this.builder, this.templateContext, graph)
        const evaluator = new NodeEvaluator(this.templateContext.localIds, mattressComponentScope, this.templateContext, this.templateInputs, activeNodeSet)

        if (this.componentDepth !== 0) {
            if (this.partialMattressMeshData === null) throw new Error("Partial mattress mesh data is null for nested component instance")
            aliasMattressMeshData(evaluator, this.partialMattressMeshData)
        }

        const context: MattressOnCompileContext & TemplateNodeContext = {
            type: "onCompile",
            ...getEmptyCompileContextData(
                evaluator,
                ROOT_TEMPLATE_GRAPH_STUB,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                this.componentDepth + 1,
                this.objectPath,
                false,
            ),
            mattressContext: {
                exposeClaimedSubComponentInputs: this.exposeClaimedSubComponentInputs,
                componentInputs: this.componentInputs,
                nodePath: this.nodePath,
                objectPath: this.objectPath,
                componentDepth: this.componentDepth + 1,
                evaluatePartialMattressMeshData: (scope: GraphBuilderScope, evaluator: NodeEvaluator) => evaluatePartialMattressMeshData(scope, evaluator),
                forwardedLocalInstanceTemplateOutputs: this.forwardedLocalInstanceTemplateOutputs,
                forwardedLocalInstanceDescriptorList: this.forwardedLocalInstanceDescriptorList,
                evaluatedData: {
                    localNodesDataLists: [],
                    referenceFramesDataLists: [],
                    curvesDataLists: [],
                    cutsDataLists: [],
                    componentsDescriptorLists: [],
                    templatesDescriptorLists: [],
                    inputSourceMappings: [],
                    componentOutputs: {},
                    subComponentsOutputs: [],
                    subTemplatesOutputs: [],
                    infoDataLists: [],
                },
            },
            skipNode: (node) => !activeNodeSet.has(node),
        }

        const resultCompile = new UncachedEvaluator(context, true)
        resultCompile.getSync(graph)

        const {mattressContext, currentTemplate} = context
        const {descriptorList: templateDescriptorList, templateOutputs, outputAffectedObjectPathParts} = currentTemplate

        const {evaluatedData} = mattressContext
        const {
            localNodesDataLists,
            curvesDataLists,
            referenceFramesDataLists,
            cutsDataLists,
            componentsDescriptorLists,
            templatesDescriptorLists,
            inputSourceMappings,
            componentOutputs,
            subComponentsOutputs,
            subTemplatesOutputs,
            infoDataLists,
        } = evaluatedData

        const cutsDataList = mattressComponentScope.merge(cutsDataLists)
        if (this.componentDepth === 0) {
            const partialMattressMeshData = evaluatePartialMattressMeshData(mattressComponentScope, evaluator)
            const fullMattressMeshData = evaluateFullMattressMeshData(mattressComponentScope, evaluator, cutsDataList, partialMattressMeshData)
            mattressComponentScope.output("fullMattressMeshData", fullMattressMeshData)
        }

        mattressComponentScope.output("localNodesDataList", mattressComponentScope.sparseMerge(localNodesDataLists))
        mattressComponentScope.output("curvesDataList", mattressComponentScope.sparseMerge(curvesDataLists))
        mattressComponentScope.output("referenceFrameDataList", mattressComponentScope.sparseMerge(referenceFramesDataLists))
        mattressComponentScope.output("cutsDataList", cutsDataList)
        mattressComponentScope.output("componentDescriptorList", mattressComponentScope.sparseMerge(componentsDescriptorLists))

        const mergedTemplateDescriptorList = mattressComponentScope.merge([
            mattressComponentScope.merge(templatesDescriptorLists),
            mattressComponentScope.list(templateDescriptorList),
        ])

        const taggedInputSourceMappings = inputSourceMappings.map((x, i) =>
            mattressComponentScope.pureLambda(
                mattressComponentScope.tuple(
                    x.rawDescriptorList,
                    mattressComponentScope.value(x.mapping, inputSourceMappingDesc, `inputSourceMapping-${i}`),
                    x.nodeId,
                ),
                ([rawDescriptors, mapping, nodeId]) => ({mapping, rawDescriptors, nodeId}),
                `taggedInputSourceMapping-${i}`,
            ),
        )

        const processedTemplateDescriptorList = mattressComponentScope.pureLambda(
            mattressComponentScope.tuple(
                mergedTemplateDescriptorList,
                mattressComponentScope.list(taggedInputSourceMappings, "taggedInputSourceMappings"),
                mattressComponentScope.value(outputAffectedObjectPathParts, outputAffectedObjectPathPartsDesc, "outputAffectedObjectPathParts"),
            ),
            ([descriptorList, taggedMappings, outputAffectedObjectPathParts]) =>
                chainDescriptors(descriptorList, taggedMappings, outputAffectedObjectPathParts),
            "processedTemplateDescriptorList",
        )

        mattressComponentScope.output("templateDescriptorList", processedTemplateDescriptorList)
        mattressComponentScope.output(
            "componentOutputs",
            mergeComponentOutputs(
                mattressComponentScope,
                [mattressComponentScope.group("componentOutputs", componentOutputs), ...subComponentsOutputs],
                "componentOutputs",
            ),
        )
        mattressComponentScope.output(
            "templateOutputs",
            mergeComponentOutputs(
                mattressComponentScope,
                [mattressComponentScope.group("templateOutputs", templateOutputs), ...subTemplatesOutputs],
                "templateOutputs",
            ),
        )
        mattressComponentScope.output("infoDataList", mattressComponentScope.sparseMerge(infoDataLists))

        const bindFn = this.builder.finalizeChanges()
        const runFn = bindFn(this)
        runFn()
    }
}
